import { z } from "zod";

/**
 * Shared DTO cho Hàng đợi yêu cầu PII/GDPR (US-AE3.F7-0014 · @UC9026 pii-gdpr-queue). MỘT nguồn sự thật
 * cho api (S01) + admin web (S03). @gate pii + schema (NEW model PrivacyRequest).
 *
 * Mỗi PrivacyRequest = 1 yêu cầu xuất (export) hoặc xoá (deletion) dữ liệu cá nhân theo GDPR.
 *
 * BẤT BIẾN (invariants):
 * - kind ∈ {export, deletion}; status ∈ {pending, fulfilled, failed} (fail-closed enum).
 * - slaBreached tính ở SERVER (read-time): slaDueAt < now && status === 'pending'. UI KHÔNG tự tính giờ.
 * - resultUrl = presigned GET URL TTL hữu hạn (export đã fulfill) HOẶC null — KHÔNG URL vĩnh viễn (AC5).
 *   Server lưu OBJECT KEY R2, serve presigned URL ở read-path.
 * - deletion REUSE guard tài chính (anonymize) — blocked ⇒ status=failed + blockedReason (mã lỗi, no-PII thô).
 * - `.strict()` mọi schema — chống over-post / field lạ.
 */

// ============================================================
// Enum-lite (khớp badge STATUS admin)
// ============================================================

/** Loại yêu cầu GDPR: export (xuất dữ liệu) | deletion (xoá/ẩn danh qua anonymize). */
export const privacyRequestKindSchema = z.enum(["export", "deletion"]);
export type PrivacyRequestKind = z.infer<typeof privacyRequestKindSchema>;

/**
 * Trạng thái vòng đời:
 *  - pending: đã tiếp nhận, chờ xử lý (SLA đếm từ đây).
 *  - processing: đã CLAIM (1 caller đang xử lý — chống double-fulfill đồng thời; trạng thái tạm).
 *  - fulfilled: đã xử lý xong (export → resultUrl set; deletion → user anonymized).
 *  - failed: xử lý thất bại (deletion bị guard tài chính chặn → blockedReason set).
 */
export const privacyRequestStatusSchema = z.enum(["pending", "processing", "fulfilled", "failed"]);
export type PrivacyRequestStatus = z.infer<typeof privacyRequestStatusSchema>;

// ============================================================
// List item (GET /admin/privacy-requests)
// ============================================================

/**
 * 1 dòng yêu cầu trên board. `code` = mã ngắn hiển thị (server derive từ id). `userId` con trỏ (nullable).
 * `requesterEmail` là email người GỬI yêu cầu (định danh yêu cầu, không phải bản ghi user — hiển thị cho
 * OPS đối soát). `resultUrl` = presigned GET URL (export fulfilled) | null. `blockedReason` = mã lỗi (deletion
 * failed) | null. `.strict()`.
 */
export const privacyRequestItemSchema = z
  .object({
    id: z.string(),
    code: z.string(),
    userId: z.string().nullable(),
    requesterEmail: z.string(),
    kind: privacyRequestKindSchema,
    status: privacyRequestStatusSchema,
    slaDueAt: z.string(),
    slaBreached: z.boolean(),
    resultUrl: z.string().nullable(),
    blockedReason: z.string().nullable(),
    createdAt: z.string(),
    fulfilledAt: z.string().nullable(),
  })
  .strict();

export type PrivacyRequestItem = z.infer<typeof privacyRequestItemSchema>;

/** Envelope list: items + cursor pageInfo `{ data, pageInfo }`. */
export const privacyRequestListResponseSchema = z
  .object({
    data: z.array(privacyRequestItemSchema),
    pageInfo: z
      .object({
        hasNext: z.boolean(),
        nextCursor: z.string().nullable(),
      })
      .strict(),
  })
  .strict();

export type PrivacyRequestListResponse = z.infer<typeof privacyRequestListResponseSchema>;

// ============================================================
// Query (GET /admin/privacy-requests)
// ============================================================

export const PRIVACY_REQUEST_LIST_MAX_LIMIT = 100;

/** Query list — filter status/kind, cursor + limit. `.strict()` chặn param lạ. */
export const privacyRequestListQuerySchema = z
  .object({
    status: privacyRequestStatusSchema.optional(),
    kind: privacyRequestKindSchema.optional(),
    cursor: z.string().max(512).optional(),
    limit: z.coerce.number().int().min(1).max(PRIVACY_REQUEST_LIST_MAX_LIMIT).optional(),
  })
  .strict();

export type PrivacyRequestListQuery = z.infer<typeof privacyRequestListQuerySchema>;

// ============================================================
// Fulfill (POST /admin/privacy-requests/:id/fulfill)
// ============================================================

export const PRIVACY_FULFILL_REASON_MAX_LENGTH = 2000;

/**
 * Body fulfill. reason optional — ghi chú audit; với deletion truyền cho anonymize (reason ghi audit
 * user_anonymized). status/kind server-set từ bản ghi (KHÔNG nhận từ client). `.strict()`.
 */
export const fulfillPrivacyRequestSchema = z
  .object({
    reason: z.string().max(PRIVACY_FULFILL_REASON_MAX_LENGTH).optional(),
  })
  .strict();

export type FulfillPrivacyRequestBody = z.infer<typeof fulfillPrivacyRequestSchema>;
